package com.eladfinish.bible.contest.ui.components

import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.gestures.ScrollableState
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.grid.LazyGridState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.RectangleShape
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.PointerType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.input.pointer.positionChange
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.unit.Density
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.dp
import com.eladfinish.bible.contest.platform.hasMousePointer
import com.eladfinish.bible.contest.platform.isWeb
import com.eladfinish.bible.contest.ui.common.accentColor
import io.github.oikvpqya.compose.fastscroller.HorizontalScrollbar
import io.github.oikvpqya.compose.fastscroller.ScrollbarStyle
import io.github.oikvpqya.compose.fastscroller.ThumbStyle
import io.github.oikvpqya.compose.fastscroller.TrackStyle
import io.github.oikvpqya.compose.fastscroller.rememberScrollbarAdapter

private val MouseWheelStep = 64.dp

fun appScrollbarStyle(thickness: Dp = 8.dp) = ScrollbarStyle(
    minimalHeight = 16.dp,
    thickness = thickness,
    hoverDurationMillis = 300,
    thumbStyle = ThumbStyle(
        shape = RoundedCornerShape(4.dp),
        unhoverColor = accentColor.copy(alpha = 0.35f),
        hoverColor = accentColor.copy(alpha = 0.65f),
    ),
    trackStyle = TrackStyle(
        shape = RectangleShape,
        unhoverColor = Color.Transparent,
        hoverColor = Color.Transparent,
    ),
)

/**
 * Whether a fastscroller scrollbar may be put on a track [trackSize] pixels long.
 *
 * fastscroller 0.3.2 never makes the thumb shorter than [ScrollbarStyle.minimalHeight]. On a track
 * shorter than that the thumb overhangs it, the library's scroll scale turns negative and the next
 * drag of the thumb throws from `SliderAdapter.onDragDelta` ("Cannot coerce value to an empty
 * range: maximum -45.0 is less than minimum 0.0"). The search results list gets that short on a
 * phone with the keyboard open (Crashlytics issue 1be9ca397113804db9051dcc2576016c). Callers leave
 * the scrollbar out then; a thumb needs at least its own length of track to move in anyway, so
 * nothing usable is lost.
 */
@Composable
fun ScrollbarStyle.fitsTrack(trackSize: Int): Boolean = fitsTrack(trackSize, LocalDensity.current)

/** [fitsTrack] with an explicit [density]: the rule itself, for callers and tests outside composition. */
fun ScrollbarStyle.fitsTrack(trackSize: Int, density: Density): Boolean =
    trackSize >= 2 * with(density) { minimalHeight.roundToPx() }

/**
 * Lets a plain mouse wheel scroll a horizontally scrolling row. A wheel only reports a vertical
 * delta, which [androidx.compose.foundation.horizontalScroll] ignores unless Shift is held.
 *
 * Put it before `horizontalScroll(state)` in the chain. The event is consumed only when the row
 * actually moved, so at either end the wheel falls through to a vertically scrolling parent.
 * Trackpad swipes that already carry a horizontal delta are left alone.
 */
fun Modifier.mouseWheelHorizontalScroll(state: ScrollState): Modifier =
    pointerInput(state) {
        awaitPointerEventScope {
            while (true) {
                val event = awaitPointerEvent()
                if (event.type != PointerEventType.Scroll) continue
                val change = event.changes.firstOrNull() ?: continue
                val delta = change.scrollDelta
                if (change.isConsumed || delta.x != 0f || delta.y == 0f) continue

                // The browser reports the wheel in CSS pixels; the other platforms in wheel steps.
                val distance = delta.y * if (isWeb) density else MouseWheelStep.toPx()
                val consumed = state.dispatchRawDelta(distance)
                if (consumed != 0f) change.consume()
            }
        }
    }

/**
 * Puts a draggable scrollbar under a horizontally scrolling [content] when the user has a mouse
 * (see [hasMousePointer]) and the content overflows. On touch screens it only emits [content].
 *
 * @param layoutDirection the direction [content] is laid out in. Pass it when the content forces
 * its own direction (e.g. [RightToLeftRow]), so the thumb moves the same way as the content.
 */
@Composable
fun HorizontalScrollbarContainer(
    scrollState: ScrollState,
    modifier: Modifier = Modifier,
    layoutDirection: LayoutDirection = LocalLayoutDirection.current,
    content: @Composable () -> Unit,
) {
    Column(modifier = modifier) {
        content()

        val style = appScrollbarStyle(thickness = 6.dp)
        val canScroll = scrollState.canScrollForward || scrollState.canScrollBackward
        if (hasMousePointer && canScroll && style.fitsTrack(scrollState.viewportSize)) {
            CompositionLocalProvider(LocalLayoutDirection provides layoutDirection) {
                HorizontalScrollbar(
                    modifier = Modifier.fillMaxWidth(),
                    adapter = rememberScrollbarAdapter(scrollState = scrollState),
                    style = style,
                    enablePressToScroll = false,
                )
            }
        }
    }
}

private val ScrollbarThickness = 8.dp
private val ScrollbarHitWidth = 12.dp
private val ScrollbarMinThumb = 24.dp

/** Size of the whole scrollable content and how far into it the viewport is, in pixels. */
private class ScrollMetrics(val contentSize: Float, val position: Float)

/**
 * `verticalScroll` plus [mouseVerticalScrollbar], for the common case of a column that owns its
 * scroll state.
 */
@Composable
fun Modifier.verticalScrollWithScrollbar(state: ScrollState = rememberScrollState()): Modifier =
    mouseVerticalScrollbar(state).verticalScroll(state)

/**
 * Draws a draggable scrollbar over the right edge when the user has a mouse (see
 * [hasMousePointer]) and the content overflows. On touch screens it does nothing.
 *
 * Must come before `verticalScroll(state)` in the chain, so it stays fixed while the content moves.
 */
@Composable
fun Modifier.mouseVerticalScrollbar(state: ScrollState): Modifier =
    mouseVerticalScrollbar(state) { viewport ->
        ScrollMetrics(contentSize = viewport + state.maxValue, position = state.value.toFloat())
            .takeIf { state.maxValue != Int.MAX_VALUE }
    }

/** A lazy list only knows its visible items, so the thumb is sized from their average height. */
@Composable
fun Modifier.mouseVerticalScrollbar(state: LazyListState): Modifier =
    mouseVerticalScrollbar(state) { _ ->
        val info = state.layoutInfo
        val visible = info.visibleItemsInfo
        if (visible.isEmpty()) return@mouseVerticalScrollbar null

        val itemSize = visible.sumOf { it.size }.toFloat() / visible.size + info.mainAxisItemSpacing
        ScrollMetrics(
            contentSize = itemSize * info.totalItemsCount + info.beforeContentPadding + info.afterContentPadding,
            position = state.firstVisibleItemIndex * itemSize + state.firstVisibleItemScrollOffset,
        )
    }

@Composable
fun Modifier.mouseVerticalScrollbar(state: LazyGridState): Modifier =
    mouseVerticalScrollbar(state) { _ ->
        val info = state.layoutInfo
        val rows = info.visibleItemsInfo.groupBy { it.row }.values
        if (rows.isEmpty()) return@mouseVerticalScrollbar null

        val itemsPerRow = rows.maxOf { it.size }
        val rowSize = rows.sumOf { row -> row.maxOf { it.size.height } }.toFloat() / rows.size +
                info.mainAxisItemSpacing
        val totalRows = (info.totalItemsCount + itemsPerRow - 1) / itemsPerRow
        ScrollMetrics(
            contentSize = rowSize * totalRows + info.beforeContentPadding + info.afterContentPadding,
            position = (state.firstVisibleItemIndex / itemsPerRow) * rowSize + state.firstVisibleItemScrollOffset,
        )
    }

@Composable
private fun Modifier.mouseVerticalScrollbar(
    state: ScrollableState,
    metrics: (viewport: Float) -> ScrollMetrics?,
): Modifier {
    if (!hasMousePointer) return this

    var isActive by remember { mutableStateOf(false) }

    // Top and height of the thumb, and how many content pixels one pixel of thumb travel is worth.
    fun thumb(viewport: Float, minThumb: Float): Triple<Float, Float, Float>? {
        if (!state.canScrollForward && !state.canScrollBackward) return null
        // No room for a thumb: a list measured at zero height (the first frame, or a collapsed
        // pane) — and coerceIn below throws when its range is empty.
        if (viewport <= minThumb) return null
        val current = metrics(viewport) ?: return null
        val scrollRange = current.contentSize - viewport
        val height = (viewport * viewport / current.contentSize).coerceIn(minThumb, viewport)
        val travel = viewport - height
        if (scrollRange <= 0f || travel <= 0f) return null

        val fraction = if (state.canScrollForward) (current.position / scrollRange).coerceIn(0f, 1f) else 1f
        return Triple(fraction * travel, height, scrollRange / travel)
    }

    return this
        .pointerInput(state) {
            awaitPointerEventScope {
                var dragRatio: Float? = null
                while (true) {
                    // Initial pass: take the press before the content under the scrollbar does.
                    val event = awaitPointerEvent(PointerEventPass.Initial)
                    val change = event.changes.firstOrNull() ?: continue
                    if (change.type != PointerType.Mouse) continue

                    val isOverScrollbar = event.type != PointerEventType.Exit &&
                            change.position.x >= size.width - ScrollbarHitWidth.toPx()
                    val ratio = dragRatio
                    if (ratio != null) {
                        if (change.pressed) {
                            state.dispatchRawDelta(change.positionChange().y * ratio)
                        } else {
                            dragRatio = null
                        }
                        change.consume()
                    } else if (event.type == PointerEventType.Press && isOverScrollbar) {
                        val (top, height, newRatio) =
                            thumb(size.height.toFloat(), ScrollbarMinThumb.toPx()) ?: continue
                        val y = change.position.y
                        // A press on the track brings the thumb under the pointer first.
                        if (y < top || y > top + height) {
                            state.dispatchRawDelta((y - (top + height / 2)) * newRatio)
                        }
                        dragRatio = newRatio
                        change.consume()
                    }
                    isActive = dragRatio != null || isOverScrollbar
                }
            }
        }
        .drawWithContent {
            drawContent()

            val (top, height) = thumb(size.height, ScrollbarMinThumb.toPx()) ?: return@drawWithContent
            val thickness = ScrollbarThickness.toPx()
            drawRoundRect(
                color = accentColor.copy(alpha = if (isActive) 0.65f else 0.35f),
                topLeft = Offset(size.width - thickness, top),
                size = Size(thickness, height),
                cornerRadius = CornerRadius(thickness / 2),
            )
        }
}
